import React, { useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './rich-text-editor.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/** Extract a YouTube video id from any common URL/embed form, or return null. */
function extractYouTubeId(input) {
  if (!input) return null;
  const str = String(input).trim();
  // youtu.be/VIDEOID
  let m = str.match(/youtu\.be\/([A-Za-z0-9_-]{6,20})/);
  if (m) return m[1];
  // youtube.com/watch?v=VIDEOID
  m = str.match(/[?&]v=([A-Za-z0-9_-]{6,20})/);
  if (m) return m[1];
  // youtube.com/embed/VIDEOID or /shorts/VIDEOID or /v/VIDEOID
  m = str.match(/youtube\.com\/(?:embed|shorts|v)\/([A-Za-z0-9_-]{6,20})/);
  if (m) return m[1];
  // Bare 11-char id
  if (/^[A-Za-z0-9_-]{11}$/.test(str)) return str;
  return null;
}

/**
 * WYSIWYG editor used across the site (forum, news, vehicles, events).
 * - Value is HTML string.
 * - Supports inline image upload and YouTube embed at the cursor position.
 */
export default function RichTextEditor({ value, onChange, placeholder, minHeight = 180, dataTestid }) {
  const quillRef = useRef(null);
  const fileInputRef = useRef(null);

  const getEditor = () => quillRef.current && quillRef.current.getEditor();

  const insertAtCursor = useCallback((type, payload) => {
    const editor = getEditor();
    if (!editor) return;
    const range = editor.getSelection(true);
    const index = range ? range.index : editor.getLength();
    editor.insertEmbed(index, type, payload, 'user');
    editor.setSelection(index + 1, 0, 'user');
    // Blank line after media for easier typing
    editor.insertText(index + 1, '\n', 'user');
    editor.setSelection(index + 2, 0, 'user');
  }, []);

  const handleImageUpload = useCallback(async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Sadece resim dosyası eklenebilir');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Resim 5MB üstünde olamaz');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = r.data.url.startsWith('http') ? r.data.url : `${BACKEND}${r.data.url}`;
      insertAtCursor('image', url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Resim yüklenemedi');
    }
  }, [insertAtCursor]);

  const openImagePicker = useCallback(() => {
    // Save selection before opening picker so we can restore cursor
    const editor = getEditor();
    const savedRange = editor ? editor.getSelection() : null;
    const input = fileInputRef.current;
    if (!input) return;
    input.value = '';
    input.onchange = async () => {
      if (editor && savedRange) editor.setSelection(savedRange.index, savedRange.length);
      const file = input.files && input.files[0];
      await handleImageUpload(file);
    };
    input.click();
  }, [handleImageUpload]);

  const openYouTubePrompt = useCallback(() => {
    const editor = getEditor();
    const savedRange = editor ? editor.getSelection() : null;
    // eslint-disable-next-line no-alert
    const url = window.prompt('YouTube video adresini yapıştır (örn: https://youtu.be/XXXX)');
    if (!url) return;
    const id = extractYouTubeId(url);
    if (!id) {
      toast.error('Geçerli bir YouTube bağlantısı değil');
      return;
    }
    if (editor && savedRange) editor.setSelection(savedRange.index, savedRange.length);
    insertAtCursor('video', `https://www.youtube.com/embed/${id}`);
  }, [insertAtCursor]);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ color: [] }, { background: [] }],
        ['blockquote', 'code-block'],
        ['link', 'image', 'video'],
        ['clean'],
      ],
      handlers: {
        image: openImagePicker,
        video: openYouTubePrompt,
      },
    },
    clipboard: { matchVisual: false },
  }), [openImagePicker, openYouTubePrompt]);

  const formats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'color', 'background',
    'blockquote', 'code-block', 'link', 'image', 'video',
  ];

  return (
    <div className="rte-wrapper" data-testid={dataTestid} style={{ ['--rte-min-height']: `${minHeight}px` }}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        style={{ display: 'none' }}
        data-testid="rte-image-input"
      />
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ''}
        onChange={onChange}
        placeholder={placeholder}
        modules={modules}
        formats={formats}
      />
    </div>
  );
}
